import { lazy, Suspense } from 'react';
import { PermissionGuard } from '@emfood/shared';
import { Outlet, createRoute, SearchSchemaInput } from '@tanstack/react-router';

import DashboardLayout from 'src/layouts/dashboard';
import { ROLES, PERMISSIONS } from 'src/api/roles-config';

import AppAuthGuard from 'src/components/auth/app-auth-guard';
import BuyerGuard from 'src/components/auth/guard/buyer-guard';
import { SplashScreen, LoadingScreen } from 'src/components/loading-screen';

import { BuyerSiteSearchParamsSchema } from 'src/sections/buyers/buyer-sites/search-params';
import { BuyersListSearchParamsSchema } from 'src/sections/buyers/buyers-list/search-params';
import { BuyerDetailsSearchParamsSchema } from 'src/sections/buyers/buyer-details/search-params';

import { rootRoute } from './root';

const BuyersList = lazy(() => import('src/sections/buyers/buyers-list/view'));
const AddBuyer = lazy(() => import('src/sections/buyers/add-buyer/view'));
const BuyerDetails = lazy(() => import('src/sections/buyers/buyer-details/view'));
const EditBuyerSite = lazy(() => import('src/sections/buyers/buyer-sites/view'));

// Buyers routes
const buyersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'buyers',
  component: () => (
    <AppAuthGuard splashScreen={<SplashScreen />}>
      <DashboardLayout>
        <Suspense fallback={<LoadingScreen />}>
          <Outlet />
        </Suspense>
      </DashboardLayout>
    </AppAuthGuard>
  ),
});

const buyersIndexRoute = createRoute({
  getParentRoute: () => buyersRoute,
  path: '/',
  validateSearch: (search: unknown & SearchSchemaInput) =>
    BuyersListSearchParamsSchema.validateSync(search),
  component: () => (
    <PermissionGuard requiredPermissions={[PERMISSIONS.BUYER_ORGS_READ]}>
      <BuyersList />
    </PermissionGuard>
  ),
});

const addBuyerRoute = createRoute({
  getParentRoute: () => buyersRoute,
  path: 'add',
  component: () => (
    <PermissionGuard requiredPermissions={[PERMISSIONS.BUYER_ORGS_WRITE]}>
      <AddBuyer />
    </PermissionGuard>
  ),
});

const buyerDetailsRoute = createRoute({
  getParentRoute: () => buyersRoute,
  path: '$id',
  validateSearch: (search: unknown & SearchSchemaInput) =>
    BuyerDetailsSearchParamsSchema.validateSync(search),
  component: () => {
    const { id } = buyerDetailsRoute.useParams();
    return (
      <BuyerGuard
        orgId={id}
        alwaysAllowRoles={[ROLES.DATA_MANAGER, ROLES.DISTRIBUTOR_ADMIN, ROLES.ENTEGRA_USER]}
        requiredRoles={[ROLES.ADMIN, ROLES.BUYER_ADMIN, ROLES.ENTEGRA_PRICE_ADMIN]}
      >
        <BuyerDetails />
      </BuyerGuard>
    );
  },
});

const editBuyerSiteRoute = createRoute({
  getParentRoute: () => buyersRoute,
  path: '$buyerOrgId/sites/$buyerSiteId',
  validateSearch: (search: unknown & SearchSchemaInput) =>
    BuyerSiteSearchParamsSchema.validateSync(search),
  component: () => {
    const { buyerOrgId } = editBuyerSiteRoute.useParams();
    return (
      <BuyerGuard
        orgId={buyerOrgId}
        requiredRoles={[
          ROLES.ADMIN,
          ROLES.ENTEGRA_USER,
          ROLES.BUYER_ADMIN,
          ROLES.ENTEGRA_PRICE_ADMIN,
          ROLES.DISTRIBUTOR_ADMIN,
          ROLES.DATA_MANAGER,
        ]}
      >
        <EditBuyerSite />
      </BuyerGuard>
    );
  },
});

export const buyersRootTree = buyersRoute.addChildren([
  buyersIndexRoute,
  addBuyerRoute,
  buyerDetailsRoute,
  editBuyerSiteRoute,
]);
